import {
  ShowBase,
  useNotify,
  useRecordContext,
  useTranslate,
  useUpdate,
} from "ra-core";
import { Link } from "react-router";
import { ReferenceField } from "@/components/admin/reference-field";
import { EditButton } from "@/components/admin/edit-button";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

import {
  DownloadInvoiceButton,
  PreviewInvoiceButton,
} from "./DownloadInvoiceButton";
import { SignatureSection } from "./SignatureSection";
import { formatUsd, itemsSubtotal, type Shipment } from "./types";

export const ShipmentShow = () => (
  <ShowBase>
    <ShipmentShowContent />
  </ShowBase>
);

const ShipmentShowContent = () => {
  const r = useRecordContext<Shipment>();
  const translate = useTranslate();
  if (!r) return null;
  const subtotal = itemsSubtotal(r.items);
  return (
    <div className="mt-2 max-w-4xl mx-auto w-full px-4 md:px-0">
      <Card>
        <CardContent className="flex flex-col gap-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-2xl font-semibold tabular-nums">
                  {r.invoice_number}
                </h2>
                {r.status === "void" ? (
                  <Badge variant="outline">
                    {translate("egi.status.void")}
                  </Badge>
                ) : null}
                {r.trade_type === "intercompany" ? (
                  <Badge
                    variant="outline"
                    className="border-sky-600/50 text-sky-700"
                  >
                    {translate("egi.trade_type.intercompany")}
                  </Badge>
                ) : null}
              </div>
              <div className="text-sm text-muted-foreground">
                {new Date(`${r.invoice_date}T00:00:00`).toLocaleDateString()}
                {" · "}
                <ReferenceField
                  source="sales_id"
                  reference="sales"
                  link={false}
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <PreviewInvoiceButton size="default" />
              <DownloadInvoiceButton size="default" />
              {/* A signed invoice is locked; an admin clears the signature first. */}
              {r.signed_at ? null : <EditButton />}
              {r.signed_at ? null : <VoidButton />}
            </div>
          </div>

          <SignatureSection />

          <div>
            <div className="text-sm text-muted-foreground">
              {translate("egi.fields.customer")}
            </div>
            <ReferenceField
              source="company_id"
              reference="companies"
              link="show"
            >
              <CustomerName />
            </ReferenceField>
          </div>

          <table className="w-full text-sm">
            <thead>
              <tr className="border-b-2 border-foreground text-left">
                <th className="py-2 font-semibold">
                  {translate("egi.fields.product")}
                </th>
                <th className="py-2 font-semibold text-right w-28">
                  {translate("egi.fields.quantity")}
                </th>
                <th className="py-2 font-semibold text-right w-28">
                  {translate("egi.fields.unit_price")}
                </th>
                <th className="py-2 font-semibold text-right w-32">
                  {translate("egi.fields.amount")}
                </th>
              </tr>
            </thead>
            <tbody>
              {r.items.map((it, i) => (
                <tr key={i} className="border-b">
                  <td className="py-3">
                    <div className="font-medium">{it.description}</div>
                    {it.details ? (
                      <div className="text-muted-foreground">{it.details}</div>
                    ) : null}
                  </td>
                  <td className="py-3 text-right tabular-nums">
                    {Number(it.quantity).toLocaleString()}
                  </td>
                  <td className="py-3 text-right tabular-nums">
                    {formatUsd(Number(it.unit_price))}
                  </td>
                  <td className="py-3 text-right tabular-nums">
                    {formatUsd(Number(it.quantity) * Number(it.unit_price))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="ml-auto w-64 text-sm flex flex-col gap-1">
            <Row
              label={translate("egi.fields.subtotal")}
              value={formatUsd(subtotal)}
            />
            <Row
              label={translate("egi.fields.tax")}
              value={formatUsd(Number(r.tax))}
            />
            <div className="flex justify-between border-t-2 border-[#FF6A00] pt-2 mt-1 text-lg font-semibold">
              <span>{translate("egi.fields.total")}</span>
              <span className="tabular-nums">
                {formatUsd(subtotal + Number(r.tax || 0))}
              </span>
            </div>
          </div>
          {r.notes ? (
            <div className="text-sm">
              <div className="text-muted-foreground">
                {translate("egi.fields.notes")}
              </div>
              <p className="whitespace-pre-wrap">{r.notes}</p>
            </div>
          ) : null}
          <Link to="/shipments" className="text-sm text-muted-foreground">
            {translate("egi.shipments.back")}
          </Link>
        </CardContent>
      </Card>
    </div>
  );
};

const Row = ({ label, value }: { label: string; value: string }) => (
  <div className="flex justify-between">
    <span className="text-muted-foreground">{label}</span>
    <span className="tabular-nums">{value}</span>
  </div>
);

const CustomerName = () => {
  const c = useRecordContext();
  return c ? <span className="text-lg font-medium">{c.name}</span> : null;
};

const VoidButton = () => {
  const r = useRecordContext<Shipment>();
  const translate = useTranslate();
  const notify = useNotify();
  const [update, { isPending }] = useUpdate();
  if (!r) return null;
  const isVoid = r.status === "void";
  const toggle = () => {
    if (
      !isVoid &&
      !window.confirm(
        translate("egi.shipments.void_confirm", { number: r.invoice_number }),
      )
    )
      return;
    update(
      "shipments",
      {
        id: r.id,
        data: { status: isVoid ? "active" : "void" },
        previousData: r,
      },
      {
        mutationMode: "pessimistic",
        onSuccess: () =>
          notify(isVoid ? "egi.shipments.restored" : "egi.shipments.voided"),
        onError: (e: unknown) =>
          notify(
            e instanceof Error ? e.message : "egi.shipments.update_failed",
            {
              type: "error",
            },
          ),
      },
    );
  };
  return (
    <Button variant="outline" onClick={toggle} disabled={isPending}>
      {translate(isVoid ? "egi.shipments.restore" : "egi.shipments.void")}
    </Button>
  );
};
